import {produce} from 'immer'

import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'
import cloneDeep from 'lodash/cloneDeep'
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import {BS} from '../../../../types/BS_types'
import {defaultMatrixBuildParameter} from '../MatrixParamBuild.consts'
import type {MatrixParamBuildParameter, SelectItemMatrixParameter} from '../MatrixParamBuild.types'

import type {
  MatrixParamBuildContextProps,
  MatrixParamBuildProviderProps,
} from './MatrixParamBuildContext.types'
import {useMatrixParamBuildError} from './MatrixParamBuildErrorContext'

const MatrixParamBuildContext = createContext<MatrixParamBuildContextProps>(
  {} as MatrixParamBuildContextProps,
)

export const useMatrixParamBuildContext = () => useContext(MatrixParamBuildContext)

function MatrixParamBuildProvider({
  data,
  options,
  onSubmit,
  onViweAsCodeSubmit,
  children,
  buildTypeId,
}: MatrixParamBuildProviderProps) {
  const elementSaveButtonRef = useRef<HTMLInputElement | undefined>(
    BS?.BuildFeatureDialog?.getSaveButton(),
  )
  const {hasFieldsHideErrorHandler} = useMatrixParamBuildError()
  const [parameters, setParameters] = React.useState<(MatrixParamBuildParameter | null)[]>(
    cloneDeep(data),
  )
  const restoreParametersRef = useRef<Record<string, MatrixParamBuildParameter | undefined>>({})
  const restoreParameters = restoreParametersRef.current
  const parametersNameSet = useMemo(
    () => new Set(parameters?.map(parameter => parameter?.name)),
    [parameters],
  )
  const parameterSelectHints = useMemo(() => new Map<string, string | undefined>(), [])
  const parameterSelectOptions = useMemo(
    () =>
      options.reduce<SelectItemMatrixParameter[]>((accumulator, parameter) => {
        const {name: label, description} = parameter
        const isDublicate = parametersNameSet.has(parameter.name)
        if (!isDublicate) {
          const keyword = [parameter.name, label, ...(parameter?.keyword ?? [])]
          accumulator.push({
            label,
            key: parameter.name,
            keyword,
            details: parameter?.description,
          })
        }

        parameterSelectHints.set(label, description)

        return accumulator
      }, []),
    [options, parameterSelectHints, parametersNameSet],
  )
  const parameterValuesOptions = useMemo(
    () =>
      options.reduce<Map<string, Omit<MatrixParamBuildParameter, 'value'> & {value: SelectItem[]}>>(
        (accumulator, parameter) => {
          accumulator.set(parameter.name, {
            ...parameter,
            value:
              parameter?.value?.map(({value, label}) => {
                const labelKey = label ? `${label}=>` : ''
                const key = `${labelKey}${value}`

                return {
                  key,
                  label: key,
                }
              }) ?? [],
          })

          return accumulator
        },
        new Map(),
      ),
    [options],
  )
  const parameterDuplicates = useMemo(
    () =>
      parameters.reduce<Map<string, number>>((accumulator, parameter) => {
        const parameterName = parameter?.name
        if (parameterName) {
          let amount = accumulator.get(parameterName)
          accumulator.set(parameterName, typeof amount === 'number' ? ++amount : 0)
        }

        return accumulator
      }, new Map()),
    [parameters],
  )
  const deleteParameterHandler = useCallback(
    (parameter: MatrixParamBuildParameter, index: number) => {
      restoreParameters[index] = parameter

      setParameters(state =>
        produce(state, draft => {
          draft[index] = null
        }),
      )
    },
    [restoreParameters],
  )
  const restoreParameterHandler = useCallback(
    (index: number) => {
      const restoredParameter = restoreParameters[index]

      restoreParameters[index] = undefined

      if (restoredParameter) {
        setParameters(state =>
          produce(state, draft => {
            draft[index] = restoredParameter
          }),
        )
      }
    },
    [restoreParameters],
  )
  const deletePermanentlyParameterHandler = useCallback(
    (index: number) => {
      restoreParameters[index] = undefined

      setParameters(state =>
        produce(state, draft => {
          delete draft[index]
        }),
      )
    },
    [restoreParameters],
  )
  const addParameterHandler = useCallback(
    () => setParameters([...parameters, cloneDeep(defaultMatrixBuildParameter)]),
    [parameters],
  )
  const addParameterValueHandler = useCallback(
    (index: number) => {
      const parameterValues = parameters[index]?.value

      if (parameterValues != null) {
        setParameters(state =>
          produce(state, draft => {
            draft[index]?.value.push({
              value: '',
            })
          }),
        )
      }
    },
    [parameters],
  )
  const deleteParameterValueHandler = useCallback(
    (parameterIndex: number, index: number) => {
      const parameterValues = parameters[parameterIndex]?.value

      if (parameterValues != null) {
        setParameters(state =>
          produce(state, draft => {
            const parameter = draft[parameterIndex]
            if (parameter != null && 'value' in parameter) {
              parameter.value = parameterValues.filter((_, indexValue) => indexValue !== index)
            }
          }),
        )
      }
    },
    [parameters],
  )
  const saveButtonClickHandler = useCallback(
    (event: MouseEvent) => {
      const hasHideError = hasFieldsHideErrorHandler()

      event.stopPropagation()
      event.preventDefault()

      if (!hasHideError) {
        const saveParameters = parameters.filter(
          (parameter): parameter is MatrixParamBuildParameter => parameter != null,
        )

        onSubmit(saveParameters)
      }
    },
    [hasFieldsHideErrorHandler, onSubmit, parameters],
  )
  const viewAsCodeHandler = useCallback(
    async (version: string, portable: boolean) => {
      const saveParameters = parameters.filter(
        (parameter): parameter is MatrixParamBuildParameter => parameter != null,
      )

      return await onViweAsCodeSubmit(saveParameters, version, portable)
    },
    [onViweAsCodeSubmit, parameters],
  )
  const valueContext = useMemo(
    () => ({
      parameters,
      buildTypeId,
      restoreParameters,
      parameterSelectOptions,
      parameterValuesOptions,
      parameterDuplicates,
      parameterSelectHints,
      setParameters,
      addParameterHandler,
      deleteParameterHandler,
      restoreParameterHandler,
      addParameterValueHandler,
      deleteParameterValueHandler,
      deletePermanentlyParameterHandler,
    }),
    [
      addParameterHandler,
      addParameterValueHandler,
      buildTypeId,
      deleteParameterHandler,
      deleteParameterValueHandler,
      deletePermanentlyParameterHandler,
      parameterDuplicates,
      parameterSelectOptions,
      parameterValuesOptions,
      parameters,
      parameterSelectHints,
      restoreParameterHandler,
      restoreParameters,
    ],
  )

  useEffect(() => {
    const elementSaveButton = elementSaveButtonRef.current

    elementSaveButton?.addEventListener('click', saveButtonClickHandler)
    BS?.BuildFeatureDialog?.recenterDialog()
    BS?.EditDsl?.setCustomLoadDslFragment(viewAsCodeHandler)

    return () => {
      elementSaveButton?.removeEventListener('click', saveButtonClickHandler)
    }
  }, [elementSaveButtonRef, saveButtonClickHandler, viewAsCodeHandler])

  return (
    <MatrixParamBuildContext.Provider value={valueContext}>
      {children}
    </MatrixParamBuildContext.Provider>
  )
}

export default memo<MatrixParamBuildProviderProps>(MatrixParamBuildProvider)
